<script context="module" lang="ts">
  export type Card = "thread" | "reply" | "post";
</script>

<script lang="ts">
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import CreationTimeInterval from "$components/+blocks/Time/TimeAgo.svelte";
  import { forum } from "$stores/forum";
  import AuthorDetails from "./ForumFields/AuthorDetails.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import ToolTip from "$components/+blocks/Tooltip/Tooltip.svelte";
  import Action, { type DropdownAction } from "$components/+blocks/Dropdown/fields/Action.svelte";
  import cx from "classnames";
  import type { Author } from "models/forums";
  import {
    Right,
    LockSolid,
    PinFilled,
    EyeSlash,
    VerticalDots,
    ExpandedListFilled,
  } from "$components/+blocks/OVIcon";

  export let type: Card;
  export let author: Author | null;
  export let time: string = "";
  export let modified: string = "";
  export let highlight: boolean = false;
  export let isQuote: boolean = false;
  export let pinned: boolean = false;
  export let locked: boolean = false;
  export let hidden: boolean = false;
  export let maximized: boolean = false;
  export let fields: DropdownAction[];
  export let isAuthorReply: boolean = false;
  export let isSolving: boolean = false;
  export let showReplies: boolean = false;
  export let showBreadCrumb: boolean = false;
  export let crumb: string = "";
  export let crumbColour: string = "";
  export let showChannelColour: boolean = false;
</script>

<div
  class={cx(
    "border-[0.5px] bg-white p-4 shadow transition-colors dark:bg-white/3",
    { "border-l-2": showBreadCrumb },
    { "rounded-lg": !isQuote && !showReplies },
    { "rounded-t-lg": !isQuote && showReplies },
    {
      "rounded-md border-l-4 border-l-neutral-400/20 bg-neutral-400/10 dark:border-l-neutral-500 dark:bg-white/5":
        isQuote,
      "hover:bg-black/3 dark:hover:bg-white/8": isQuote,
      "hover:bg-black/2 dark:hover:bg-white/5": !isQuote && type !== "post",
      "border-informational-green/70 dark:border-informational-green/40":
        $forum.forumSpace.solvableThreads && isSolving && !highlight,
      "border-black/10 dark:border-white/10": !highlight && !isSolving,
      "border-community-primary": highlight,
    }
  )}
  style={showChannelColour ? `border-left-color: ${crumbColour};` : ""}
>
  <div class="mb-2 flex flex-wrap-reverse justify-between gap-2">
    <!-- not-prose to stop editor from mangling image alignment -->
    <div class="not-prose mr-4 inline-flex flex-1 flex-wrap items-center gap-2">
      {#if author}
        <Avatar
          mainImage={author.profileImage}
          backgroundColor={author.profileColor}
          label={author.handle && author.handle[0]}
          size="size-6"
          textSize="text-sm"
          radius="rounded"
        />
        <AuthorDetails {author} {type} showAuthorBadge={type === "reply" && isAuthorReply} />
      {/if}

      {#if showBreadCrumb}
        <div
          class="hidden items-center gap-2 text-sm font-normal text-neutral-500 sm:flex md:text-[13px] dark:text-neutral-400"
        >
          <OVIcon icon={Right} size="size-3" />
          {crumb}
        </div>
      {/if}
    </div>

    <div class="mr-0.5 flex items-center gap-2.5 self-end">
      {#if maximized}
        <ToolTip>
          <span slot="tip">Thread card maximized</span>
          <OVIcon icon={ExpandedListFilled} class="text-neutral-400" />
        </ToolTip>
      {/if}
      {#if locked}
        <ToolTip>
          <span slot="tip">Thread locked</span>
          <OVIcon icon={LockSolid} class="text-neutral-400" />
        </ToolTip>
      {/if}
      {#if pinned}
        <ToolTip>
          <span slot="tip">Pinned</span>
          <OVIcon icon={PinFilled} class="text-neutral-400" />
        </ToolTip>
      {/if}
      {#if hidden}
        <span class="text-[#F5656C]">
          <OVIcon icon={EyeSlash} class="mr-0.5" />
        </span>
      {/if}
      <slot name="solutionStatus" />

      <CreationTimeInterval {time} {modified} showEdited />

      {#if !!fields?.length}
        <Dropdown>
          <IconButtonTrigger
            customIconSize="h-[18px]"
            slot="trigger"
            let:active
            {active}
            icon={VerticalDots}
            a11yLabel="More actions"
          />
          {#each fields as field}
            <Action title={field.title} icon={field.icon} on:click={field.onClick} />
          {/each}
        </Dropdown>
      {/if}
    </div>
  </div>
  <slot />
</div>
